/**
 * 관리자 계정(admin_user) React Query 훅.
 * 목록·상세·CRUD·보안 액션·메뉴 권한·계정 감사 로그를 캐시 키와 함께 제공한다.
 */
import { adminUserService } from '@/entities/admin-user/api/admin-user.api';
import { auditLogService } from '@/entities/admin-user/api/audit-log.api';
import {
  userAccessLogService,
  type UserAccessLogListParams,
} from '@/entities/admin-user/api/access-log.api';
import type {
  AdminAuditLogListParams,
  AdminUserCreatePayload,
  AdminUserDeletePayload,
  AdminUserListParams,
  AdminUserSecurityActionPayload,
  AdminUserUpdatePayload,
  MenuPermissionSaveItem,
} from '@/entities/admin-user/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/** 관리자 계정 캐시 키 — 목록/상세/권한/감사로그 invalidate 공통 */
export const adminUserKeys = {
  all: ['admin-users'] as const,
  lists: () => [...adminUserKeys.all, 'list'] as const,
  list: (params: AdminUserListParams) =>
    [...adminUserKeys.lists(), params] as const,
  details: () => [...adminUserKeys.all, 'detail'] as const,
  detail: (id: number) => [...adminUserKeys.details(), id] as const,
  menuPermissions: (id: number) =>
    [...adminUserKeys.all, 'menu-permissions', id] as const,
  menuPermissionsMetadata: (targetRoleName?: string) =>
    [
      ...adminUserKeys.all,
      'menu-permissions-metadata',
      targetRoleName ?? 'default',
    ] as const,
  auditLogs: (id: number) => [...adminUserKeys.all, 'audit-logs', id] as const,
};

export const auditLogKeys = {
  all: ['audit-logs'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (params: AdminAuditLogListParams) =>
    [...auditLogKeys.lists(), params] as const,
};

export const userAccessLogKeys = {
  all: ['user-access-logs'] as const,
  lists: () => [...userAccessLogKeys.all, 'list'] as const,
  list: (params: UserAccessLogListParams) =>
    [...userAccessLogKeys.lists(), params] as const,
};

/**
 * 관리자 계정 목록.
 * Spring Page는 0-based `page` — 호출부에서 UI(1-based)를 변환해 전달한다.
 */
export const useAdminUserList = (params: AdminUserListParams) =>
  useQuery({
    queryKey: adminUserKeys.list(params),
    queryFn: () => adminUserService.list(params),
  });

export const useAdminUserDetail = (id: number | null) =>
  useQuery({
    queryKey: id != null ? adminUserKeys.detail(id) : [],
    queryFn: async () => {
      if (id == null) throw new Error('adminUserId is required');
      return adminUserService.get(id);
    },
    enabled: id != null && id > 0,
  });

export const useCreateAdminUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AdminUserCreatePayload) => adminUserService.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
    },
  });
};

export const useUpdateAdminUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: AdminUserUpdatePayload;
    }) => adminUserService.update(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(id) });
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.menuPermissions(id),
      });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.auditLogs(id) });
    },
  });
};

export const useRemoveAdminUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: AdminUserDeletePayload;
    }) => adminUserService.remove(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(id) });
    },
  });
};

export const useUnlockAdminUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: AdminUserSecurityActionPayload;
    }) => adminUserService.unlock(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.auditLogs(id) });
    },
  });
};

export const useResetAdminUserTemporaryPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: AdminUserSecurityActionPayload;
    }) => adminUserService.resetTemporaryPassword(id, body),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.auditLogs(id) });
    },
  });
};

export const useAdminUserMenuPermissions = (
  id: number | null,
  enabled = true,
) =>
  useQuery({
    queryKey: id != null ? adminUserKeys.menuPermissions(id) : [],
    queryFn: async () => {
      if (id == null) throw new Error('adminUserId is required');
      return adminUserService.getMenuPermissions(id);
    },
    enabled: enabled && id != null && id > 0,
  });

export const useSaveAdminUserMenuPermissions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      permissions,
    }: {
      id: number;
      permissions: MenuPermissionSaveItem[];
    }) => adminUserService.saveMenuPermissions(id, permissions),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: adminUserKeys.menuPermissions(id),
      });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.auditLogs(id) });
    },
  });
};

export const useAdminMenuPermissionsMetadata = (
  enabled = true,
  targetRoleName?: string,
) =>
  useQuery({
    queryKey: adminUserKeys.menuPermissionsMetadata(targetRoleName),
    queryFn: () =>
      adminUserService.getMenuPermissionsMetadata(targetRoleName),
    enabled,
  });

/** 계정별 감사 로그 (최대 200건, 페이징 없음) */
export const useAdminUserAuditLogs = (id: number | null, enabled = true) =>
  useQuery({
    queryKey: id != null ? adminUserKeys.auditLogs(id) : [],
    queryFn: async () => {
      if (id == null) throw new Error('adminUserId is required');
      return adminUserService.getAuditLogs(id);
    },
    enabled: enabled && id != null && id > 0,
  });

/** 전역 감사 로그 목록 — Spring page 0-based */
export const useAuditLogList = (params: AdminAuditLogListParams) =>
  useQuery({
    queryKey: auditLogKeys.list(params),
    queryFn: () => auditLogService.list(params),
  });

/** 접속 로그 — 관리자 계정 상세에서는 userType=ADMIN + userId */
export const useUserAccessLogList = (
  params: UserAccessLogListParams,
  enabled = true,
) =>
  useQuery({
    queryKey: userAccessLogKeys.list(params),
    queryFn: () => userAccessLogService.list(params),
    enabled,
  });
